"use client";

import { useEffect, useMemo, useState } from "react";
import { strToU8, zipSync } from "fflate";
import {
  BUILDS_API_PATH,
  READINESS_API_PATH,
  bearerHeaders,
  isBuildJob,
  isOneTimeCredentials,
  isProvisionerReadiness,
  isValidAdminEmail,
  isValidDevLinksDomain,
  isValidSiteSlug,
  normalizeSiteSlug,
  type BuildJob,
  type OneTimeCredentials,
  type ProvisionerReadiness,
} from "./provisioning-contract";

type PageChoice = {
  id: string;
  name: string;
  description: string;
  sections: string[];
  required?: boolean;
};

type StylePreset = {
  id: string;
  name: string;
  description: string;
  colors: [string, string, string, string];
  heading: string;
  body: string;
};

type PreparedPackage = {
  file: File;
  fingerprint: string;
  sha256: string;
};

const pageChoices: PageChoice[] = [
  {
    id: "home",
    name: "Home",
    description: "Primary story, services overview and conversion path.",
    sections: ["Hero", "Value proposition", "Services", "Proof", "Primary CTA"],
    required: true,
  },
  {
    id: "about",
    name: "About",
    description: "Company story, point of view, values and credibility.",
    sections: ["Intro hero", "Story", "Values", "Proof", "CTA"],
  },
  {
    id: "services",
    name: "Services",
    description: "A clear overview of everything the business offers.",
    sections: ["Services hero", "Service grid", "Process", "FAQ", "CTA"],
  },
  {
    id: "service-detail",
    name: "Service detail",
    description: "Reusable detail-page system for each core service.",
    sections: ["Service hero", "Benefits", "How it works", "Proof", "FAQ", "CTA"],
  },
  {
    id: "team",
    name: "Team",
    description: "People, roles, biographies and human credibility.",
    sections: ["Team intro", "People grid", "Credentials", "Culture", "CTA"],
  },
  {
    id: "process",
    name: "How it works",
    description: "Make the buying or onboarding process easy to understand.",
    sections: ["Process hero", "Steps", "Expectations", "FAQ", "CTA"],
  },
  {
    id: "case-studies",
    name: "Case studies",
    description: "Outcomes, examples, testimonials and supporting proof.",
    sections: ["Results hero", "Featured story", "Results grid", "Testimonials", "CTA"],
  },
  {
    id: "faq",
    name: "FAQ",
    description: "A focused objection-handling and answer page.",
    sections: ["FAQ intro", "Question groups", "Still need help", "CTA"],
  },
  {
    id: "blog",
    name: "Insights",
    description: "Article archive and reusable single-post templates.",
    sections: ["Insights hero", "Featured post", "Article grid", "Newsletter CTA"],
  },
  {
    id: "contact",
    name: "Contact",
    description: "Contact routes, form, location and next-step expectations.",
    sections: ["Contact hero", "Form", "Contact details", "Map or service area", "FAQ"],
  },
  {
    id: "landing",
    name: "Campaign landing",
    description: "A focused, navigation-light lead-generation page.",
    sections: ["Offer hero", "Benefits", "Proof", "Objections", "Lead form"],
  },
  {
    id: "locations",
    name: "Locations",
    description: "Location index plus a repeatable local page pattern.",
    sections: ["Locations hero", "Location grid", "Service area", "Directions", "CTA"],
  },
];

const stylePresets: StylePreset[] = [
  {
    id: "editorial",
    name: "Clean editorial",
    description: "Quiet confidence, generous spacing and refined type.",
    colors: ["#17324D", "#F4EFE7", "#CB774F", "#FFFFFF"],
    heading: "Cormorant Garamond",
    body: "Outfit",
  },
  {
    id: "modern",
    name: "Modern studio",
    description: "Crisp structure, bold headlines and graphic contrast.",
    colors: ["#101828", "#F7F8FA", "#F05A3C", "#B8D8D8"],
    heading: "Manrope",
    body: "Inter",
  },
  {
    id: "warm",
    name: "Warm local",
    description: "Approachable, grounded and intentionally human.",
    colors: ["#29443B", "#F6F0E5", "#D99158", "#D8E2D7"],
    heading: "DM Serif Display",
    body: "DM Sans",
  },
  {
    id: "technical",
    name: "Technical minimal",
    description: "Precise, restrained and highly information-forward.",
    colors: ["#121B25", "#F3F5F7", "#3274F0", "#D6E4FF"],
    heading: "IBM Plex Sans",
    body: "IBM Plex Sans",
  },
];

const steps = ["Pages", "Brand", "Content", "Build"];

export function SiteConfigurator() {
  const [step, setStep] = useState(0);
  const [selectedPages, setSelectedPages] = useState<string[]>([
    "home",
    "services",
    "about",
    "faq",
    "contact",
  ]);
  const [styleId, setStyleId] = useState("editorial");
  const [styleMode, setStyleMode] = useState("upload");
  const [styleUrl, setStyleUrl] = useState("");
  const [styleFiles, setStyleFiles] = useState<File[]>([]);
  const [siteName, setSiteName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#17324D");
  const [accentColor, setAccentColor] = useState("#CB774F");
  const [headingFont, setHeadingFont] = useState("Cormorant Garamond");
  const [bodyFont, setBodyFont] = useState("Outfit");
  const [context, setContext] = useState("");
  const [audience, setAudience] = useState("");
  const [services, setServices] = useState("");
  const [tone, setTone] = useState("Clear, confident, human");
  const [primaryCtaLabel, setPrimaryCtaLabel] = useState("Get started");
  const [primaryCtaUrl, setPrimaryCtaUrl] = useState("/contact/");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagery, setImagery] = useState("provided");
  const [siteSlug, setSiteSlug] = useState("");
  const [requestedDomain, setRequestedDomain] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [buildAccessKey, setBuildAccessKey] = useState("");
  const [packageState, setPackageState] = useState<"idle" | "preparing" | "ready" | "error">("idle");
  const [preparedPackage, setPreparedPackage] = useState<PreparedPackage | null>(null);
  const [confirmedFingerprint, setConfirmedFingerprint] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<ProvisionerReadiness | null>(null);
  const [isCheckingReadiness, setIsCheckingReadiness] = useState(false);
  const [provisionJob, setProvisionJob] = useState<BuildJob | null>(null);
  const [oneTimeCredentials, setOneTimeCredentials] = useState<OneTimeCredentials | null>(null);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [provisionError, setProvisionError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selected = useMemo(
    () => pageChoices.filter((page) => selectedPages.includes(page.id)),
    [selectedPages],
  );
  const selectedStyle = stylePresets.find((preset) => preset.id === styleId)!;
  const sectionCount = selected.reduce((sum, page) => sum + page.sections.length, 0);
  const resolvedSiteSlug = siteSlug || normalizeSiteSlug(siteName);
  const packageFingerprint = useMemo(() => JSON.stringify({
    selectedPages,
    styleId,
    styleMode,
    styleUrl,
    styleFiles: styleFiles.map(fileFingerprint),
    siteName,
    primaryColor,
    accentColor,
    headingFont,
    bodyFont,
    context,
    audience,
    services,
    tone,
    primaryCtaLabel,
    primaryCtaUrl,
    contactEmail,
    contactPhone,
    logoFile: logoFile ? fileFingerprint(logoFile) : null,
    imageFiles: imageFiles.map(fileFingerprint),
    imagery,
    siteSlug,
  }), [
    selectedPages, styleId, styleMode, styleUrl, styleFiles, siteName, primaryColor,
    accentColor, headingFont, bodyFont, context, audience, services, tone,
    primaryCtaLabel, primaryCtaUrl, contactEmail, contactPhone, logoFile, imageFiles,
    imagery, siteSlug,
  ]);
  const packageIsCurrent = packageState === "ready" && preparedPackage?.fingerprint === packageFingerprint;
  const resolvedDomain = requestedDomain.trim().toLowerCase() || `${resolvedSiteSlug}.dev-links.com`;
  const buildDetailsReady = isValidSiteSlug(resolvedSiteSlug)
    && isValidAdminEmail(adminEmail)
    && isValidDevLinksDomain(resolvedDomain)
    && buildAccessKey.trim().length > 0;
  const buildConfirmationKey = JSON.stringify({ packageFingerprint, resolvedDomain, adminEmail: adminEmail.trim().toLowerCase() });
  const buildConfirmed = confirmedFingerprint === buildConfirmationKey;
  const provisionIsActive = Boolean(provisionJob && ["queued", "running"].includes(provisionJob.state));
  const canStartProvision = Boolean(packageIsCurrent && buildDetailsReady && readiness?.ready && buildConfirmed && !provisionIsActive && !isSubmitting);
  const activeJobId = provisionJob?.id;
  const activeJobState = provisionJob?.state;
  const selectedSiteNeedsForm = selectedPages.some((page) => ["contact", "landing"].includes(page));
  const stepReady = [
    selected.length > 0,
    siteName.trim().length > 1,
    context.trim().length > 30
      && audience.trim().length > 2
      && services.trim().length > 2
      && primaryCtaLabel.trim().length > 1
      && (!selectedSiteNeedsForm || isValidAdminEmail(contactEmail)),
    true,
  ][step];

  useEffect(() => {
    if (!activeJobId || !activeJobState || !["queued", "running"].includes(activeJobState) || !buildAccessKey) return;
    let stopped = false;
    const poll = async () => {
      try {
        const response = await fetch(`${BUILDS_API_PATH}/${encodeURIComponent(activeJobId)}`, {
          headers: { Accept: "application/json", ...bearerHeaders(buildAccessKey.trim()) },
          cache: "no-store",
        });
        const body: unknown = await response.json();
        if (!response.ok || !isBuildJob(body)) throw new Error(safeApiMessage(body, "The build status could not be read."));
        if (!stopped) {
          setProvisionJob(body);
          setProvisionError("");
        }
      } catch (error) {
        if (!stopped) setProvisionError(error instanceof Error ? error.message : "The build status could not be read.");
      }
    };
    const timer = window.setInterval(poll, 2500);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [activeJobId, activeJobState, buildAccessKey]);

  function togglePage(id: string) {
    if (id === "home") return;
    setSelectedPages((current) =>
      current.includes(id) ? current.filter((page) => page !== id) : [...current, id],
    );
  }

  function chooseStyle(preset: StylePreset) {
    setStyleId(preset.id);
    setPrimaryColor(preset.colors[0]);
    setAccentColor(preset.colors[2]);
    setHeadingFont(preset.heading);
    setBodyFont(preset.body);
  }

  async function preparePackage() {
    setPackageState("preparing");
    setProvisionError("");
    try {
      const prepared = await createBuildPackage();
      setPreparedPackage(prepared);
      setPackageState("ready");
    } catch (error) {
      setPackageState("error");
      setProvisionError(error instanceof Error ? error.message : "The build package could not be prepared.");
    }
  }

  async function createBuildPackage(): Promise<PreparedPackage> {
    const payload = {
      schema_version: 1,
      site: {
        key: resolvedSiteSlug,
        name: siteName,
        status: "draft",
        page_template: "elementor_header_footer",
      },
      design_system: {
        reference: {
          mode: styleMode,
          url: styleUrl,
          files: styleFiles.map((file) => `references/${file.name}`),
          instruction: "Extract the visual language and adapt it across the selected sitemap; do not duplicate the reference content.",
        },
        direction: selectedStyle.id,
        colors: { primary: primaryColor, accent: accentColor },
        typography: { heading: headingFont, body: bodyFont },
      },
      content_context: {
        summary: context,
        audience,
        services,
        tone,
        verified_facts_only: true,
        generation_rule: "Do not invent testimonials, statistics, credentials, pricing, locations, or regulated claims.",
      },
      brand: {
        logo: logoFile ? { key: "brand-logo", source: `assets/${logoFile.name}`, alt: `${siteName} logo` } : null,
        primary_cta: { label: primaryCtaLabel, url: primaryCtaUrl },
        email: contactEmail,
        phone: contactPhone,
      },
      imagery: {
        mode: imagery,
        assets: imageFiles.map((file, index) => ({
          key: `site-image-${index + 1}`,
          source: `assets/${file.name}`,
          alt: "",
          rights_confirmed: false,
        })),
      },
      navigation: selected
        .filter((page) => page.id !== "landing")
        .map((page) => ({ label: page.name, url: page.id === "home" ? "/" : `/${page.id}/` })),
      footer: { summary: context, copyright: `${siteName}. All rights reserved.` },
      pages: selected.map((page) => ({
        type: page.id,
        title: page.name,
        slug: page.id === "home" ? "home" : page.id,
        sections: page.sections,
      })),
      generation: {
        create_global_kit: true,
        create_header_footer: true,
        create_navigation: true,
        import_status: "draft",
      },
    };
    const entries: Record<string, Uint8Array> = {
      "project.json": strToU8(JSON.stringify(payload, null, 2)),
    };
    for (const file of styleFiles) entries[`references/${file.name}`] = new Uint8Array(await file.arrayBuffer());
    if (logoFile) entries[`assets/${logoFile.name}`] = new Uint8Array(await logoFile.arrayBuffer());
    for (const file of imageFiles) entries[`assets/${file.name}`] = new Uint8Array(await file.arrayBuffer());
    const zipped = zipSync(entries, { level: 6 });
    const hashInput = new Uint8Array(zipped.byteLength);
    hashInput.set(zipped);
    const digest = await crypto.subtle.digest("SHA-256", hashInput.buffer);
    const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    const filename = `${payload.site.key || "elementor-site"}-build-package.zip`;
    return {
      file: new File([zipped as BlobPart], filename, { type: "application/zip" }),
      fingerprint: packageFingerprint,
      sha256,
    };
  }

  function downloadPreparedPackage() {
    if (!preparedPackage || !packageIsCurrent) return;
    const url = URL.createObjectURL(preparedPackage.file);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = preparedPackage.file.name;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  async function checkReadiness() {
    if (!buildAccessKey.trim()) return;
    setIsCheckingReadiness(true);
    setProvisionError("");
    try {
      const response = await fetch(READINESS_API_PATH, {
        headers: { Accept: "application/json", ...bearerHeaders(buildAccessKey.trim()) },
        cache: "no-store",
      });
      const body: unknown = await response.json();
      if (!response.ok || !isProvisionerReadiness(body)) throw new Error(safeApiMessage(body, "The provisioner readiness check failed."));
      setReadiness(body);
    } catch (error) {
      setReadiness(null);
      setProvisionError(error instanceof Error ? error.message : "The provisioner readiness check failed.");
    } finally {
      setIsCheckingReadiness(false);
    }
  }

  async function startProvision() {
    if (!canStartProvision || !preparedPackage) return;
    setIsSubmitting(true);
    setProvisionError("");
    setOneTimeCredentials(null);
    try {
      const response = await fetch(BUILDS_API_PATH, {
        method: "POST",
        headers: {
          "Content-Type": "application/zip",
          "X-Site-Domain": resolvedDomain,
          "X-Site-Name": encodeURIComponent(siteName.trim()),
          "X-Admin-Email": adminEmail.trim(),
          ...bearerHeaders(buildAccessKey.trim()),
        },
        body: preparedPackage.file,
      });
      const body: unknown = await response.json();
      if (!response.ok || !isBuildJob(body)) throw new Error(safeApiMessage(body, "The site build could not be started."));
      setProvisionJob(body);
      setConfirmedFingerprint(null);
    } catch (error) {
      setProvisionError(error instanceof Error ? error.message : "The site build could not be started.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function revealCredentials() {
    if (!provisionJob || provisionJob.state !== "succeeded" || !buildAccessKey) return;
    setIsLoadingCredentials(true);
    setProvisionError("");
    try {
      const response = await fetch(`${BUILDS_API_PATH}/${encodeURIComponent(provisionJob.id)}/credentials`, {
        headers: { Accept: "application/json", ...bearerHeaders(buildAccessKey.trim()) },
        cache: "no-store",
      });
      const body: unknown = await response.json();
      if (!response.ok || !isOneTimeCredentials(body)) throw new Error(safeApiMessage(body, "The one-time login could not be retrieved."));
      setOneTimeCredentials(body);
    } catch (error) {
      setProvisionError(error instanceof Error ? error.message : "The one-time login could not be retrieved.");
    } finally {
      setIsLoadingCredentials(false);
    }
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <a className="brand-lockup" href="#top" aria-label="Site Foundry home">
          <span className="brand-mark">SF</span>
          <span>
            <strong>Site Foundry</strong>
            <small>Elementor suite builder</small>
          </span>
        </a>
        <div className="topbar-status">
          <span className="status-dot" />
          Draft-first workflow
        </div>
      </header>

      <div className="workspace" id="top">
        <aside className="step-rail" aria-label="Builder progress">
          <p className="rail-label">Build setup</p>
          <ol>
            {steps.map((label, index) => (
              <li key={label}>
                <button
                  className={index === step ? "active" : index < step ? "complete" : ""}
                  disabled={provisionIsActive}
                  onClick={() => setStep(index)}
                  type="button"
                >
                  <span>{index < step ? "✓" : index + 1}</span>
                  {label}
                </button>
              </li>
            ))}
          </ol>
          <div className="rail-note">
            <strong>What one run creates</strong>
            <p>Global styles, header, footer, navigation, responsive pages, reusable sections and review drafts.</p>
          </div>
        </aside>

        <section className="builder-panel">
          {step === 0 && (
            <div className="step-content">
              <div className="section-heading">
                <p className="eyebrow">01 · Choose the site</p>
                <h1>Which pages do you need?</h1>
                <p>Select the pages. The system chooses the right sections and builds a complete first-pass sitemap.</p>
              </div>

              <div className="selection-bar">
                <span><strong>{selected.length}</strong> pages selected</span>
                <span>{sectionCount} planned sections</span>
                <button
                  type="button"
                  onClick={() => setSelectedPages(["home", "services", "about", "faq", "contact"])}
                >
                  Use recommended 5-page set
                </button>
              </div>

              <div className="page-grid">
                {pageChoices.map((page) => {
                  const isSelected = selectedPages.includes(page.id);
                  return (
                    <button
                      key={page.id}
                      type="button"
                      className={`page-choice ${isSelected ? "selected" : ""}`}
                      onClick={() => togglePage(page.id)}
                      aria-pressed={isSelected}
                    >
                      <span className="checkmark">{isSelected ? "✓" : ""}</span>
                      <span className="page-choice-copy">
                        <span className="page-choice-title">
                          <strong>{page.name}</strong>
                          {page.required && <small>Required</small>}
                        </span>
                        <span>{page.description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="step-content">
              <div className="section-heading">
                <p className="eyebrow">02 · Give it the style</p>
                <h1>Show us what it should feel like.</h1>
                <p>Add a mockup, screenshot, PDF, Figma link, live site or Elementor template. The system converts that visual language into a complete global design system.</p>
              </div>

              <div className="reference-panel">
                <div className="reference-tabs" role="tablist" aria-label="Style reference type">
                  {[
                    ["upload", "Upload reference"],
                    ["url", "Paste a link"],
                    ["elementor", "Elementor template"],
                  ].map(([value, label]) => (
                    <button
                      type="button"
                      key={value}
                      className={styleMode === value ? "active" : ""}
                      onClick={() => setStyleMode(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {styleMode === "upload" && (
                  <label className="reference-drop">
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.fig"
                      onChange={(event) => setStyleFiles(Array.from(event.target.files || []))}
                    />
                    <span className="upload-glyph">＋</span>
                    <span>
                      <strong>{styleFiles.length ? `${styleFiles.length} reference${styleFiles.length === 1 ? "" : "s"} added` : "Drop mockups or screenshots here"}</strong>
                      <small>{styleFiles.length ? styleFiles.map((file) => file.name).join(", ") : "PNG, JPG, PDF or Figma export · multiple screens are welcome"}</small>
                    </span>
                    <b>{styleFiles.length ? "Replace" : "Browse"}</b>
                  </label>
                )}

                {styleMode === "url" && (
                  <label className="field reference-url">
                    <span>Figma or live-site URL</span>
                    <input placeholder="https://…" value={styleUrl} onChange={(event) => setStyleUrl(event.target.value)} />
                    <small>Use a publicly accessible page or share link. Multiple references can be added to the project brief later.</small>
                  </label>
                )}

                {styleMode === "elementor" && (
                  <label className="reference-drop">
                    <input
                      type="file"
                      accept="application/json,.json,.zip"
                      onChange={(event) => setStyleFiles(Array.from(event.target.files || []))}
                    />
                    <span className="upload-glyph">↥</span>
                    <span>
                      <strong>{styleFiles.length ? styleFiles[0].name : "Add an Elementor kit or page export"}</strong>
                      <small>The cleanest route when the reference already exists in Elementor</small>
                    </span>
                    <b>{styleFiles.length ? "Replace" : "Browse"}</b>
                  </label>
                )}

                <div className="extraction-row">
                  <span>Extracts</span>
                  <ul>
                    <li>Colors + type</li>
                    <li>Spacing + widths</li>
                    <li>Buttons + cards</li>
                    <li>Image treatment</li>
                    <li>Section rhythm</li>
                  </ul>
                </div>
              </div>

              <label className="field field-wide">
                <span>Site or company name</span>
                <input value={siteName} onChange={(event) => setSiteName(event.target.value)} />
              </label>

              <div className="fallback-label"><span>Fallback direction</span><small>Used only where the reference does not answer a design question.</small></div>
              <div className="style-grid">
                {stylePresets.map((preset) => (
                  <button
                    type="button"
                    key={preset.id}
                    onClick={() => chooseStyle(preset)}
                    className={`style-card ${preset.id === styleId ? "selected" : ""}`}
                    aria-pressed={preset.id === styleId}
                  >
                    <span className="palette">
                      {preset.colors.map((color) => <i key={color} style={{ background: color }} />)}
                    </span>
                    <strong>{preset.name}</strong>
                    <span>{preset.description}</span>
                    <small>{preset.heading} + {preset.body}</small>
                  </button>
                ))}
              </div>

              <div className="field-grid brand-fields">
                <label className="field color-field">
                  <span>Primary color</span>
                  <span className="color-control">
                    <input type="color" value={primaryColor} onChange={(event) => setPrimaryColor(event.target.value)} />
                    <input value={primaryColor} onChange={(event) => setPrimaryColor(event.target.value)} />
                  </span>
                </label>
                <label className="field color-field">
                  <span>Accent color</span>
                  <span className="color-control">
                    <input type="color" value={accentColor} onChange={(event) => setAccentColor(event.target.value)} />
                    <input value={accentColor} onChange={(event) => setAccentColor(event.target.value)} />
                  </span>
                </label>
                <label className="field">
                  <span>Heading font</span>
                  <select value={headingFont} onChange={(event) => setHeadingFont(event.target.value)}>
                    {["Cormorant Garamond", "Manrope", "DM Serif Display", "IBM Plex Sans", "Libre Baskerville"].map((font) => <option key={font}>{font}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span>Body font</span>
                  <select value={bodyFont} onChange={(event) => setBodyFont(event.target.value)}>
                    {["Outfit", "Inter", "DM Sans", "IBM Plex Sans", "Source Sans 3"].map((font) => <option key={font}>{font}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              <div className="section-heading">
                <p className="eyebrow">03 · Give it context</p>
                <h1>Describe the business.</h1>
                <p>Short, useful context is enough. The content engine turns it into page-specific first-draft copy.</p>
              </div>

              <div className="field-grid content-fields">
                <label className="field field-wide">
                  <span>What does the business do?</span>
                  <textarea rows={5} placeholder="Paste the client brief, discovery notes, or a rough description…" value={context} onChange={(event) => setContext(event.target.value)} />
                  <small>Paste a client brief, discovery notes or a rough paragraph.</small>
                </label>
                <label className="field">
                  <span>Primary audience</span>
                  <input placeholder="Who is this site for?" value={audience} onChange={(event) => setAudience(event.target.value)} />
                </label>
                <label className="field">
                  <span>Tone</span>
                  <input value={tone} onChange={(event) => setTone(event.target.value)} />
                </label>
                <label className="field field-wide">
                  <span>Core services or offers</span>
                  <input placeholder="List the real services the site should cover" value={services} onChange={(event) => setServices(event.target.value)} />
                </label>
                <label className="field">
                  <span>Primary action label</span>
                  <input value={primaryCtaLabel} onChange={(event) => setPrimaryCtaLabel(event.target.value)} />
                </label>
                <label className="field">
                  <span>Primary action URL</span>
                  <input value={primaryCtaUrl} onChange={(event) => setPrimaryCtaUrl(event.target.value)} />
                </label>
                <label className="field">
                  <span>Contact email</span>
                  <input type="email" placeholder="hello@example.com" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} />
                </label>
                <label className="field">
                  <span>Contact phone</span>
                  <input placeholder="Optional" value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} />
                </label>
              </div>

              <fieldset className="image-options">
                <legend>Imagery</legend>
                {[
                  ["provided", "Use provided images", "Upload or map the client’s approved assets."],
                  ["assisted", "Source a cohesive image set", "Prepare image prompts and search guidance for every planned placement."],
                  ["placeholder", "Use clean placeholders", "Build the complete layout now and swap imagery later."],
                ].map(([value, label, description]) => (
                  <label key={value} className={imagery === value ? "selected" : ""}>
                    <input type="radio" name="imagery" value={value} checked={imagery === value} onChange={() => setImagery(value)} />
                    <span><strong>{label}</strong><small>{description}</small></span>
                  </label>
                ))}
              </fieldset>

              <div className="asset-row">
                <label className="asset-upload">
                  <input type="file" accept="image/*,.svg" onChange={(event) => setLogoFile(event.target.files?.[0] || null)} />
                  <span><strong>{logoFile ? logoFile.name : "Add logo"}</strong><small>SVG, PNG or JPG</small></span>
                  <b>{logoFile ? "Replace" : "Browse"}</b>
                </label>
                <label className="asset-upload">
                  <input type="file" multiple accept="image/*" onChange={(event) => setImageFiles(Array.from(event.target.files || []))} />
                  <span><strong>{imageFiles.length ? `${imageFiles.length} image${imageFiles.length === 1 ? "" : "s"} added` : "Add site imagery"}</strong><small>{imageFiles.length ? imageFiles.map((file) => file.name).join(", ") : "Optional when using placeholders"}</small></span>
                  <b>{imageFiles.length ? "Replace" : "Browse"}</b>
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="step-content build-step">
              <div className="section-heading">
                <p className="eyebrow">04 · Build the site</p>
                <h1>Review, provision, and hand off.</h1>
                <p>Prepare the exact compiler package, confirm the real server build, then follow every stage through to the site and WordPress admin.</p>
              </div>

              <div className="build-summary">
                <div><strong>{selected.length}</strong><span>Pages</span></div>
                <div><strong>{sectionCount}</strong><span>Sections</span></div>
                <div><strong>2</strong><span>Global templates</span></div>
                <div><strong>1</strong><span>Elementor kit</span></div>
              </div>

              <section className="provision-card" aria-labelledby="build-target-heading">
                <div className="provision-card-heading">
                  <div>
                    <span>Build target</span>
                    <h2 id="build-target-heading">Where should this site live?</h2>
                  </div>
                  <b>Hetzner</b>
                </div>
                <div className="field-grid provision-fields">
                  <label className="field">
                    <span>Site slug</span>
                    <input
                      value={siteSlug}
                      placeholder={normalizeSiteSlug(siteName) || "client-site"}
                      disabled={provisionIsActive}
                      onChange={(event) => setSiteSlug(normalizeSiteSlug(event.target.value))}
                    />
                    <small>Used as the WordPress site key and default subdomain.</small>
                  </label>
                  <label className="field">
                    <span>Domain</span>
                    <input
                      value={requestedDomain}
                      placeholder={`${resolvedSiteSlug || "client-site"}.dev-links.com`}
                      disabled={provisionIsActive}
                      onChange={(event) => setRequestedDomain(event.target.value.toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, ""))}
                    />
                    <small>V1 accepts one <code>*.dev-links.com</code> hostname.</small>
                  </label>
                  <label className="field">
                    <span>WordPress admin email</span>
                    <input
                      type="email"
                      value={adminEmail}
                      placeholder="owner@example.com"
                      disabled={provisionIsActive}
                      onChange={(event) => setAdminEmail(event.target.value)}
                    />
                    <small>Receives the site handoff; it is not written into the project ZIP.</small>
                  </label>
                  <label className="field">
                    <span>Build access key</span>
                    <input
                      type="password"
                      value={buildAccessKey}
                      placeholder="Required to reach the provisioner"
                      autoComplete="off"
                      disabled={provisionIsActive}
                      onChange={(event) => {
                        setBuildAccessKey(event.target.value);
                        setReadiness(null);
                      }}
                    />
                    <small>Held in memory for this tab only. Never saved or added to the package.</small>
                  </label>
                </div>

                <div className="readiness-row">
                  <div>
                    <strong>Provisioner readiness</strong>
                    <small>{readiness ? readiness.message : "Authenticate and verify the server before starting a real build."}</small>
                  </div>
                  <span className={`readiness-badge ${readiness?.ready ? "ready" : readiness ? "blocked" : "unchecked"}`}>
                    {readiness?.ready ? "Ready" : readiness ? "Blocked" : "Not checked"}
                  </span>
                  <button type="button" onClick={checkReadiness} disabled={!buildAccessKey || isCheckingReadiness || provisionIsActive}>
                    {isCheckingReadiness ? "Checking…" : readiness ? "Check again" : "Check server"}
                  </button>
                </div>
                {readiness && (
                  <ul className="readiness-checks">
                    {readiness.checks.map((check) => (
                      <li key={check.id} className={check.status}>
                        <span>{check.status === "pass" ? "✓" : check.status === "warning" ? "!" : "×"}</span>
                        <p><strong>{check.label}</strong><small>{check.message}</small></p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="package-card" aria-labelledby="package-heading">
                <div>
                  <span className={`package-mark ${packageIsCurrent ? "ready" : ""}`}>{packageIsCurrent ? "✓" : "1"}</span>
                  <p>
                    <strong id="package-heading">Compiler package</strong>
                    <small>
                      {packageIsCurrent && preparedPackage
                        ? `${formatBytes(preparedPackage.file.size)} · SHA-256 ${preparedPackage.sha256.slice(0, 12)}…`
                        : preparedPackage ? "Inputs changed. Prepare the package again before building." : "Package the brief, references, logo, and imagery locally in this browser."}
                    </small>
                  </p>
                </div>
                <div className="package-actions">
                  {packageIsCurrent && <button type="button" className="quiet-button" onClick={downloadPreparedPackage}>Download copy</button>}
                  <button
                    type="button"
                    className="prepare-button"
                    onClick={preparePackage}
                    disabled={!isValidSiteSlug(resolvedSiteSlug) || packageState === "preparing" || provisionIsActive}
                  >
                    {packageState === "preparing" ? "Preparing…" : packageIsCurrent ? "Prepare again" : "Prepare package"}
                  </button>
                </div>
              </section>

              {!provisionJob && (
                <div className="build-confirmation">
                  <label>
                    <input
                      type="checkbox"
                      checked={buildConfirmed}
                      disabled={!packageIsCurrent || provisionIsActive}
                      onChange={(event) => setConfirmedFingerprint(event.target.checked ? buildConfirmationKey : null)}
                    />
                    <span>
                      <strong>Create a real WordPress site on Hetzner</strong>
                      <small>I confirm the domain and admin email above. Generated pages import as drafts for review.</small>
                    </span>
                  </label>
                  <button className="build-button" type="button" onClick={startProvision} disabled={!canStartProvision}>
                    {isSubmitting ? "Starting build…" : "Build site"}
                  </button>
                  {!canStartProvision && (
                    <p className="build-requirements">Required: valid slug/domain/email, access key, ready server, current package, and confirmation.</p>
                  )}
                </div>
              )}

              {provisionError && <div className="provision-error" role="alert">{provisionError}</div>}

              {provisionJob && (
                <section className="job-card" aria-live="polite" aria-labelledby="job-heading">
                  <div className="job-heading">
                    <div>
                      <span>Build {provisionJob.id}</span>
                      <h2 id="job-heading">{provisionJob.state === "succeeded" ? "Your site is ready." : provisionJob.state === "failed" ? "The build stopped." : "Building your site…"}</h2>
                      <p>{provisionJob.message}</p>
                    </div>
                    <b className={`job-state ${provisionJob.state}`}>{provisionJob.state.replace("_", " ")}</b>
                  </div>

                  <ol className="stage-list">
                    {provisionJob.stages.map((stage) => (
                      <li key={stage.id} className={stage.state}>
                        <span>{stage.state === "succeeded" ? "✓" : stage.state === "failed" ? "×" : stage.state === "running" ? "•" : ""}</span>
                        <p><strong>{stage.label}</strong>{stage.message && <small>{stage.message}</small>}</p>
                      </li>
                    ))}
                  </ol>

                  {provisionJob.state === "succeeded" && provisionJob.result && (
                    <div className="handoff-card">
                      <div>
                        <strong>Site handoff</strong>
                        <p>The site is online and the generated Elementor pages are ready for review in WordPress.</p>
                      </div>
                      <div className="handoff-links">
                        <a href={provisionJob.result.site_url} target="_blank" rel="noreferrer">Open site</a>
                        {provisionJob.result.admin_url && <a href={provisionJob.result.admin_url} target="_blank" rel="noreferrer">Open WordPress</a>}
                        {!oneTimeCredentials && (
                          <button type="button" onClick={revealCredentials} disabled={isLoadingCredentials}>
                            {isLoadingCredentials ? "Retrieving…" : "Reveal one-time login"}
                          </button>
                        )}
                      </div>
                      {oneTimeCredentials && (
                        <div className="credential-card">
                          <p><strong>Save this login now.</strong><small>It is shown once and held only in this tab&apos;s memory. Refreshing clears it.</small></p>
                          <dl>
                            <div><dt>Admin URL</dt><dd><a href={oneTimeCredentials.admin_url} target="_blank" rel="noreferrer">{oneTimeCredentials.admin_url}</a></dd></div>
                            <div><dt>Username</dt><dd><code>{oneTimeCredentials.username}</code></dd></div>
                            <div><dt>Password</dt><dd><code>{oneTimeCredentials.password}</code></dd></div>
                          </dl>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              )}
            </div>
          )}

          <div className="builder-actions">
            <button type="button" className="back-button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0 || provisionIsActive}>Back</button>
            {step < 3 && (
              <div className="continue-wrap">
                {!stepReady && <small>{step === 1 ? "Add the site name to continue." : selectedSiteNeedsForm && !isValidAdminEmail(contactEmail) ? "Add the form notification email to continue." : "Add the core business context to continue."}</small>}
                <button type="button" className="continue-button" disabled={!stepReady} onClick={() => setStep(step + 1)}>Continue <span>→</span></button>
              </div>
            )}
          </div>
        </section>

        <aside className="live-plan">
          <div className="live-plan-header">
            <div>
              <p className="rail-label">Live build plan</p>
              <h2>{siteName || "Untitled site"}</h2>
            </div>
            <span>{selected.length} pages</span>
          </div>

          <div className="mini-browser" style={{ "--preview-primary": primaryColor, "--preview-accent": accentColor } as React.CSSProperties}>
            <div className="browser-bar"><i /><i /><i /></div>
            <div className="browser-nav"><strong>{(siteName || "Site").slice(0, 18)}</strong><span /><span /><button>Get started</button></div>
            <div className="browser-hero">
              <div><small>{selectedStyle.name}</small><strong>A clear headline that earns attention.</strong><span /><span className="short" /><button>Primary action</button></div>
              <i />
            </div>
            <div className="browser-cards"><i /><i /><i /></div>
          </div>

          <div className="sitemap-list">
            {selected.map((page, index) => (
              <details key={page.id} open={index === 0}>
                <summary><span>{String(index + 1).padStart(2, "0")}</span><strong>{page.name}</strong><small>{page.sections.length} sections</small></summary>
                <ul>{page.sections.map((section) => <li key={section}>{section}</li>)}</ul>
              </details>
            ))}
          </div>
        </aside>
      </div>
    </main>
  );
}

function fileFingerprint(file: File) {
  return [file.name, file.size, file.type, file.lastModified];
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function safeApiMessage(value: unknown, fallback: string) {
  if (!value || typeof value !== "object") return fallback;
  const candidate = value as { message?: unknown; error?: unknown };
  if (typeof candidate.message === "string" && candidate.message.length < 300) return candidate.message;
  if (typeof candidate.error === "string" && candidate.error.length < 300) return candidate.error;
  if (candidate.error && typeof candidate.error === "object") {
    const nested = candidate.error as { message?: unknown };
    if (typeof nested.message === "string" && nested.message.length < 300) return nested.message;
  }
  return fallback;
}
